import type {
  Affiliation,
  BoardSlug,
  FoliageSpot,
  HarvestConfig,
  HarvestRoundPlan,
  SpawnKind,
  TomatoSpawn,
} from '@tomato/shared';
import {
  AFFILIATION_LABELS,
  BOARD_LABELS,
  generateHarvestPlan,
  isCritter,
  isHarvestable,
} from '@tomato/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../api/client';
import { Badge, Button, Notice, Spinner } from '../components/ui';
import { useSessionStore } from '../store/session';

/**
 * 토마토 따기 게임 — 언제든 들어와 딸 수 있는 밭.
 * 하루 앞 N판까지만 🍅가 붙고, 그 뒤는 연습 판이다(시간 제한 없음).
 * 서버가 준 시드로 스폰을 결정론 생성해 렌더하고(공용 코어), 결과만 제출한다.
 * 렌더링은 DOM + CSS로 충분(스폰 십수 개 수준) — Canvas를 쓰지 않는다(스펙).
 *
 * 미장센(밭 사진 레퍼런스의 느낌을 SVG 자체 제작으로): 지지대에 덩굴이 감긴 밭,
 * 라운드가 갈수록 수풀이 우거지고, 딴 토마토는 아래 바구니로 포물선을 그리며 담긴다.
 */

// ─────────────────────────────────────────────────────────────
// API 응답 타입
// ─────────────────────────────────────────────────────────────

interface HarvestStatus {
  /** 오늘 시작한 판수. */
  played: number;
  /** 아직 🍅가 붙는 판이 몇 판 남았나. 0이면 이후는 전부 연습 판. */
  rewardedLeft: number;
  rewardedPlaysPerDay: number;
}

interface StartResponse {
  sessionId: string;
  seed: number;
  playNo: number;
  rewarded: boolean;
  rewardedLeft: number;
  config: HarvestConfig;
}

interface FinishResponse {
  picked: number;
  misclicks: number;
  score: number;
  reward: number;
  fee: number;
  rewarded: boolean;
  playNo: number;
  recentPosts: { id: string; title: string; board: BoardSlug }[];
}

interface LeaderboardResponse {
  weekStart: string;
  rows: { rank: number; nickname: string; affiliation?: string; score: number }[];
  me: { rank: number; score: number } | null;
}

// ─────────────────────────────────────────────────────────────
// 일러스트 — 전부 SVG 자체 제작(스톡 이미지 금지).
// ─────────────────────────────────────────────────────────────

const TOMATO_COLORS = {
  ripe: { body: '#e23b2c', shade: '#c22a1d', light: '#f4705c' },
  // 반쯤 익은 애매한 주황 — 심리전. 따면 감점.
  half: { body: '#e89a3c', shade: '#d07d22', light: '#f5bd6e' },
  unripe: { body: '#63a844', shade: '#4d8a31', light: '#8cc46a' },
} as const;

function TomatoSvg({ kind }: { kind: keyof typeof TOMATO_COLORS }) {
  const c = TOMATO_COLORS[kind];
  return (
    <svg viewBox="0 0 64 64" className="h-full w-full" aria-hidden>
      <ellipse cx="32" cy="37" rx="24" ry="21" fill={c.body} />
      <path d="M10 40 a24 18 0 0 0 44 0 a24 21 0 0 1 -44 0 Z" fill={c.shade} opacity="0.55" />
      <ellipse cx="23" cy="29" rx="7" ry="5" fill={c.light} opacity="0.85" />
      <rect x="30.4" y="10" width="3.2" height="8" rx="1.6" fill="#5c7d3a" />
      <path d="M32 17 C26 11 18 13 14 18 C21 20 27 19 32 17 Z" fill="#6fa14a" />
      <path d="M32 17 C38 11 46 13 50 18 C43 20 37 19 32 17 Z" fill="#7cb156" />
      <path d="M32 17 C29 12 30 8 32 5 C34 8 35 12 32 17 Z" fill="#6fa14a" />
    </svg>
  );
}

/** 빨간 피망 — 토마토와 같은 빨강 계열로 낚는 함정. 세로로 길쭉한 실루엣이 힌트. */
function PepperSvg() {
  return (
    <svg viewBox="0 0 64 64" className="h-full w-full" aria-hidden>
      <path
        d="M20 22 C14 30 14 48 22 54 C26 58 30 58 32 55 C34 58 38 58 42 54 C50 48 50 30 44 22 C40 17 24 17 20 22 Z"
        fill="#d93425"
      />
      <path d="M32 20 L32 55" stroke="#b52619" strokeWidth="2.5" opacity="0.5" />
      <path d="M24 21 C22 32 22 44 25 52" stroke="#f2685a" strokeWidth="3" fill="none" opacity="0.6" />
      <path d="M30 12 C30 16 31 18 32 20 C33 18 34 16 34 10 Z" fill="#4f7a32" />
      <path d="M32 19 C27 15 24 16 21 19 C25 21 29 21 32 19 Z" fill="#5f9242" />
    </svg>
  );
}

/** 나비 — 팔랑이며 밭을 가로지른다. 건드리면 감점. */
function ButterflySvg() {
  return (
    <svg viewBox="0 0 64 64" className="h-full w-full" aria-hidden>
      <g className="harvest-flap" style={{ transformBox: 'fill-box', transformOrigin: 'center' }}>
        <path d="M30 32 C18 14 4 16 6 28 C8 38 20 42 30 38 Z" fill="#f5c445" />
        <path d="M34 32 C46 14 60 16 58 28 C56 38 44 42 34 38 Z" fill="#f5c445" />
        <path d="M30 36 C22 48 10 50 10 42 C10 35 20 32 30 34 Z" fill="#efb02e" />
        <path d="M34 36 C42 48 54 50 54 42 C54 35 44 32 34 34 Z" fill="#efb02e" />
        <circle cx="14" cy="26" r="3" fill="#fff" opacity="0.75" />
        <circle cx="50" cy="26" r="3" fill="#fff" opacity="0.75" />
      </g>
      <ellipse cx="32" cy="34" rx="3.2" ry="10" fill="#4a3d2f" />
      <path d="M30 25 C27 20 24 18 21 17 M34 25 C37 20 40 18 43 17" stroke="#4a3d2f" strokeWidth="1.8" fill="none" strokeLinecap="round" />
    </svg>
  );
}

/** 무당벌레 — 밭을 기어다닌다. 건드리면 놀라서 감점. */
function LadybugSvg() {
  return (
    <svg viewBox="0 0 64 64" className="h-full w-full" aria-hidden>
      <ellipse cx="32" cy="36" rx="18" ry="16" fill="#d8342a" />
      <circle cx="32" cy="18" r="8" fill="#26221f" />
      <path d="M32 22 L32 52" stroke="#26221f" strokeWidth="2.5" />
      <circle cx="24" cy="32" r="3.4" fill="#26221f" />
      <circle cx="40" cy="32" r="3.4" fill="#26221f" />
      <circle cx="26" cy="43" r="3" fill="#26221f" />
      <circle cx="38" cy="43" r="3" fill="#26221f" />
      <circle cx="29" cy="15" r="1.6" fill="#fff" opacity="0.9" />
      <circle cx="35" cy="15" r="1.6" fill="#fff" opacity="0.9" />
    </svg>
  );
}

function SpawnSvg({ kind }: { kind: SpawnKind }) {
  if (kind === 'pepper') return <PepperSvg />;
  if (kind === 'ladybug') return <LadybugSvg />;
  if (kind === 'butterfly') return <ButterflySvg />;
  return <TomatoSvg kind={kind} />;
}

const SPAWN_LABELS: Record<SpawnKind, string> = {
  ripe: '익은 토마토',
  unripe: '안 익은 토마토',
  half: '반쯤 익은 토마토',
  pepper: '빨간 피망',
  ladybug: '무당벌레',
  butterfly: '나비',
};

/** 수풀 덤불 — 잎 몇 장을 겹친 클러스터. front면 스폰 앞을 가린다(클릭은 통과). */
function FoliageCluster({ spot }: { spot: FoliageSpot }) {
  return (
    <div
      className="harvest-sway pointer-events-none absolute"
      style={{
        left: `${spot.xPct}%`,
        top: `${3 + spot.yPct * 0.72}%`,
        width: 88 * spot.scale,
        height: 66 * spot.scale,
        marginLeft: -44 * spot.scale,
        marginTop: -33 * spot.scale,
        zIndex: spot.front ? 20 : 1,
        animationDelay: `${spot.swayDelay}s`,
        transform: spot.flip ? 'scaleX(-1)' : undefined,
        opacity: spot.front ? 0.96 : 0.8,
      }}
      aria-hidden
    >
      <svg viewBox="0 0 88 66" className="h-full w-full">
        <path d="M12 46 C2 34 8 18 24 16 C26 6 44 2 52 12 C66 6 82 16 78 30 C88 40 78 54 64 52 C58 62 38 64 30 56 C18 58 10 54 12 46 Z" fill="#4e7d33" />
        <path d="M24 40 C18 30 26 18 38 20 C44 12 58 14 60 24 C70 26 72 40 62 44 C58 52 40 54 34 48 C28 50 24 46 24 40 Z" fill="#5f9242" />
        <path d="M34 34 C32 26 40 20 48 24 C56 22 62 30 58 36 C60 42 50 48 44 44 C38 46 34 40 34 34 Z" fill="#6ea24c" opacity="0.9" />
        <path d="M30 30 C40 28 52 30 58 36" stroke="#3f6828" strokeWidth="1.5" fill="none" opacity="0.5" />
      </svg>
    </div>
  );
}

/** 밭 배경 — 지지대에 덩굴이 감긴 이랑 + 흙바닥. 장식이라 시드와 무관하게 고정. */
function FieldBackdrop() {
  const stakes = [36, 118, 200, 282, 364];
  return (
    <svg
      viewBox="0 0 400 460"
      preserveAspectRatio="none"
      className="pointer-events-none absolute inset-0 h-full w-full"
      aria-hidden
    >
      {/* 흙바닥 이랑 */}
      <rect x="0" y="400" width="400" height="60" fill="#bd9569" />
      <path d="M0 400 Q100 392 200 400 T400 400 L400 412 L0 412 Z" fill="#a97f52" />
      <ellipse cx="70" cy="432" rx="46" ry="7" fill="#a97f52" opacity="0.7" />
      <ellipse cx="210" cy="440" rx="56" ry="8" fill="#a97f52" opacity="0.6" />
      <ellipse cx="330" cy="430" rx="40" ry="6" fill="#a97f52" opacity="0.7" />
      {/* 지지대 + 덩굴 */}
      {stakes.map((x, i) => (
        <g key={x}>
          <rect x={x - 2} y="18" width="4" height="390" rx="2" fill="#8a7355" />
          <path
            d={`M${x - 10} 400 C${x + 14} 340 ${x - 16} 290 ${x + 10} 230 C${x - 14} 170 ${x + 12} 110 ${x - 6} 40`}
            stroke="#55823a"
            strokeWidth="5"
            fill="none"
            strokeLinecap="round"
            opacity="0.85"
          />
          {[80, 150, 220, 290, 350].map((y, j) => (
            <ellipse
              key={y}
              cx={x + ((i + j) % 2 === 0 ? 13 : -13)}
              cy={y + i * 7}
              rx="11"
              ry="6"
              fill={(i + j) % 3 === 0 ? '#5f9242' : '#6ea24c'}
              opacity="0.75"
              transform={`rotate(${(i + j) % 2 === 0 ? 24 : -24} ${x} ${y})`}
            />
          ))}
        </g>
      ))}
    </svg>
  );
}

/** 아래에서 수확물을 받는 바구니. 담길 때마다 통통 튀고, 알이 쌓이는 게 보인다. */
const PILE_OFFSETS = [
  [-18, 2], [0, -2], [18, 2], [-9, -6], [9, -6], [-22, 6], [22, 6], [0, -10], [-13, 8], [13, 8],
] as const;

function Basket({ count }: { count: number }) {
  const pile = Math.min(count, PILE_OFFSETS.length);
  return (
    <div
      key={count} /* 담길 때마다 리마운트 → 통통 애니메이션 재생 */
      className={`absolute bottom-1.5 left-1/2 z-50 h-[74px] w-[132px] -translate-x-1/2 ${count > 0 ? 'harvest-basket-bump' : ''}`}
    >
      <svg viewBox="0 0 132 74" className="h-full w-full" aria-hidden>
        {/* 쌓인 토마토(바구니 테두리 뒤) */}
        {Array.from({ length: pile }, (_, i) => {
          const [dx, dy] = PILE_OFFSETS[i]!;
          return (
            <g key={i} transform={`translate(${66 + dx} ${30 + dy})`}>
              <circle r="9" fill="#e23b2c" />
              <circle cx="-3" cy="-3" r="2.6" fill="#f4705c" opacity="0.9" />
              <path d="M-3 -8 C-1 -10 1 -10 3 -8 L0 -5 Z" fill="#6fa14a" />
            </g>
          );
        })}
        {/* 바구니 몸통 */}
        <path d="M12 30 L120 30 L110 68 Q66 74 22 68 Z" fill="#c98f4e" />
        <path d="M12 30 L120 30 L118 38 L14 38 Z" fill="#b57a3c" />
        {[28, 46, 64, 82, 100].map((x) => (
          <path key={x} d={`M${x} 38 L${x - 4} 68`} stroke="#a96f33" strokeWidth="3" opacity="0.8" />
        ))}
        <path d="M16 48 L116 48 M19 58 L113 58" stroke="#8f5d2b" strokeWidth="2" opacity="0.5" />
      </svg>
      {count > 0 && (
        <span className="absolute -right-1 -top-1 rounded-full bg-tomato-600 px-2 py-0.5 text-[12px] font-bold tabular-nums text-white shadow-md">
          {count}
        </span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 플레이 화면
// ─────────────────────────────────────────────────────────────

type Phase =
  | { type: 'intro'; round: number }
  | { type: 'round'; round: number; startAt: number }
  | { type: 'submitting' };

const ROUND_INTROS: { emoji: string; tip: string }[] = [
  { emoji: '🌱', tip: '익은 토마토를 따서 바구니에 담으세요!' },
  { emoji: '🫑', tip: '빨간 피망은 토마토가 아니에요 — 감점!' },
  { emoji: '🍊', tip: '주황빛은 반쯤 익은 것. 아직이에요!' },
  { emoji: '🦋', tip: '나비·무당벌레가 날아다녀요 — 건드리면 감점!' },
  { emoji: '🌿', tip: '수풀이 우거져요. 눈 크게 뜨고!' },
  { emoji: '💨', tip: '토마토가 우수수! 금방 사라져요!' },
  { emoji: '🔥', tip: '마지막 라운드! 눈 크게 뜨고!' },
];

/** 스폰 y(0~100)를 밭 영역(위 3%~75%)으로 사상 — 아래는 바구니 자리다. */
function fieldTop(yPct: number): number {
  return 3 + yPct * 0.72;
}

/**
 * 뷰포트 크기. 모바일 판별(짧은 변 < 640)과 세로 파지 여부(h > w)를 여기서 얻는다.
 * 세로 파지면 스테이지를 CSS로 90° 돌려 가로형 풀스크린으로 플레이한다.
 */
function useViewport(): { w: number; h: number } {
  const [size, setSize] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    const on = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', on);
    window.addEventListener('orientationchange', on);
    return () => {
      window.removeEventListener('resize', on);
      window.removeEventListener('orientationchange', on);
    };
  }, []);
  return size;
}

/**
 * 지금 이 순간의 스폰 위치. 토마토는 제자리, 생물은 드리프트로 흘러간다.
 * 나비는 사인파를 얹어 팔랑팔랑 나는 궤적을 만든다. rAF마다 다시 계산되므로 부드럽다.
 */
function spawnPosition(s: TomatoSpawn, elapsed: number): { x: number; y: number } {
  if (!isCritter(s.kind)) return { x: s.xPct, y: s.yPct };
  const progress = Math.min(1, Math.max(0, (elapsed - s.appearAt) / s.lifeMs));
  let x = s.xPct + (s.driftXPct ?? 0) * progress;
  let y = s.yPct + (s.driftYPct ?? 0) * progress;
  if (s.kind === 'butterfly') y += Math.sin(progress * Math.PI * 4) * 5;
  x = Math.min(96, Math.max(4, x));
  y = Math.min(100, Math.max(0, y));
  return { x, y };
}

interface Hit {
  result: 'harvest' | 'penalty';
  at: number;
}

interface FlyingTomato {
  key: number;
  leftPct: number;
  topPct: number;
  dx: number;
  dy: number;
  scale: number;
}

interface FloatText {
  key: number;
  xPct: number;
  yPct: number;
  text: string;
  at: number;
}

function HarvestGame({
  session,
  onDone,
  onAbort,
}: {
  session: StartResponse;
  onDone: (result: FinishResponse) => void;
  onAbort: (message: string) => void;
}) {
  const { config } = session;
  // 판 전체 스폰 플랜은 시드로 한 번만 결정론 생성 — 서버와 같은 코드를 쓴다.
  const [plan] = useState<HarvestRoundPlan[]>(() => generateHarvestPlan(session.seed, config));
  const [phase, setPhase] = useState<Phase>({ type: 'intro', round: 0 });
  const [, setFrame] = useState(0); // rAF마다 갱신해 elapsed 기반 렌더를 유발한다.
  const [basketCount, setBasketCount] = useState(0);
  const [flying, setFlying] = useState<FlyingTomato[]>([]);
  const viewport = useViewport();
  // 모바일(짧은 변 < 640)은 풀스크린 스테이지로. 세로 파지면 90° 돌려 가로형을 강제한다.
  const mobileFull = Math.min(viewport.w, viewport.h) < 640;
  const rotated = mobileFull && viewport.h > viewport.w;

  const fieldRef = useRef<HTMLDivElement>(null);
  const hitsRef = useRef<Map<string, Hit>>(new Map());
  const pickedRef = useRef(0);
  const missRef = useRef(0);
  const floatsRef = useRef<FloatText[]>([]);
  const keyRef = useRef(0);
  const submittedRef = useRef(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // 풀스크린 스테이지 동안 배경 스크롤(과 당겨서 새로고침)을 잠근다.
  // iOS는 body만으론 문서(html) 고무줄이 남아 새로고침 제스처가 샌다 — html까지.
  useEffect(() => {
    if (!mobileFull) return;
    const html = document.documentElement;
    const prev = {
      bodyOverflow: document.body.style.overflow,
      htmlOverflow: html.style.overflow,
      htmlOverscroll: html.style.overscrollBehavior,
    };
    document.body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';
    return () => {
      document.body.style.overflow = prev.bodyOverflow;
      html.style.overflow = prev.htmlOverflow;
      html.style.overscrollBehavior = prev.htmlOverscroll;
    };
  }, [mobileFull]);

  // 라운드 인트로 → 잠깐 보여주고 라운드 시작.
  useEffect(() => {
    if (phase.type !== 'intro') return;
    const timer = setTimeout(
      () => setPhase({ type: 'round', round: phase.round, startAt: performance.now() }),
      1500,
    );
    return () => clearTimeout(timer);
  }, [phase]);

  // 라운드 진행 중에는 rAF로 시계를 돌린다. 스폰 십수 개 수준이라 매 프레임 렌더로 충분하다.
  useEffect(() => {
    if (phase.type !== 'round') return;
    let raf = 0;
    const loop = () => {
      setFrame((f) => f + 1);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  const submit = useCallback(async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setPhase({ type: 'submitting' });
    try {
      const res = await api.post<FinishResponse>(`/api/harvest/session/${session.sessionId}/finish`, {
        picked: pickedRef.current,
        misclicks: missRef.current,
      });
      // 잔액 배지 갱신은 백그라운드로.
      void useSessionStore.getState().refreshMe();
      onDone(res);
    } catch (err) {
      onAbort(err instanceof ApiError ? err.message : '결과를 저장하지 못했어요.');
    }
  }, [session, onDone, onAbort]);

  // 라운드 종료 판정.
  useEffect(() => {
    if (phase.type !== 'round') return;
    const elapsed = performance.now() - phase.startAt;
    if (elapsed < plan[phase.round]!.durationMs) return;
    if (phase.round + 1 < plan.length) {
      setPhase({ type: 'intro', round: phase.round + 1 });
    } else {
      void submit();
    }
  });

  if (phase.type === 'submitting') {
    return (
      <div className="py-24 text-center">
        <Spinner />
        <p className="mt-3 text-[13px] text-ink-500">바구니를 저울에 올리는 중…</p>
      </div>
    );
  }

  const round = phase.round;
  const roundPlan = plan[round]!;
  const elapsed = phase.type === 'round' ? performance.now() - phase.startAt : 0;
  const remainRatio = Math.max(0, 1 - elapsed / roundPlan.durationMs);

  /** 클릭 처리. 익은 것이면 바구니로 날리고, 아니면 그 자리에서 부르르 떨며 감점. */
  function pick(spawnId: string, kind: SpawnKind, xPct: number, yPct: number, scale: number) {
    if (phase.type !== 'round' || hitsRef.current.has(spawnId)) return;
    const now = performance.now();

    if (isHarvestable(kind)) {
      hitsRef.current.set(spawnId, { result: 'harvest', at: now });
      pickedRef.current += 1;

      // 시작점→바구니 입구까지의 px 벡터를 재서 포물선 비행 CSS 변수로 넘긴다.
      // clientWidth/Height(레이아웃 좌표)를 쓴다 — 가로형 회전(transform) 중에도 정확하다.
      const el = fieldRef.current;
      if (el) {
        const sx = (xPct / 100) * el.clientWidth;
        const sy = (fieldTop(yPct) / 100) * el.clientHeight;
        const key = (keyRef.current += 1);
        setFlying((f) => [
          ...f,
          {
            key,
            leftPct: xPct,
            topPct: fieldTop(yPct),
            dx: el.clientWidth / 2 - sx,
            dy: el.clientHeight - 44 - sy,
            scale,
          },
        ]);
        // 착지 = 비행 애니메이션 길이. animationend 대신 타이머 — 모션 최소화 환경에서도 담긴다.
        setTimeout(() => {
          if (!aliveRef.current) return;
          setFlying((f) => f.filter((t) => t.key !== key));
          setBasketCount((n) => n + 1);
        }, 620);
      } else {
        setBasketCount((n) => n + 1);
      }
    } else {
      hitsRef.current.set(spawnId, { result: 'penalty', at: now });
      missRef.current += 1;
      floatsRef.current.push({
        key: (keyRef.current += 1),
        xPct,
        yPct,
        text: `−${config.penalty}`,
        at: now,
      });
    }
  }

  const now = performance.now();
  floatsRef.current = floatsRef.current.filter((f) => now - f.at < 700);

  const stage = (
    <div className={mobileFull ? 'mx-auto flex h-full max-w-3xl flex-col px-4 py-3' : 'py-6'}>
      {/* HUD — 숫자는 최소한으로. 수확량은 아래 바구니가 말해준다. */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge tone="accent">
            {round + 1}/{plan.length}라운드
          </Badge>
          <span className="text-[13px] text-ink-500">{session.playNo}판째</span>
        </div>
        <span className="text-[12px] text-ink-400">함정을 조심하세요</span>
      </div>

      {/* 남은 시간 바 */}
      <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-ink-100">
        <div
          className="h-full rounded-full bg-tomato-500 transition-none"
          style={{ width: `${remainRatio * 100}%` }}
        />
      </div>

      {/* 밭 */}
      <div
        ref={fieldRef}
        className={`relative touch-manipulation select-none overflow-hidden rounded-2xl bg-gradient-to-b from-[#e8f3da] via-[#dcedcc] to-[#d2e5bf] shadow-card ${
          mobileFull ? 'min-h-0 flex-1' : 'h-[460px]'
        }`}
      >
        <FieldBackdrop />

        {phase.type === 'intro' ? (
          <div className="harvest-intro relative z-30 flex h-full flex-col items-center justify-center gap-2">
            <span className="text-[44px]">{ROUND_INTROS[round]?.emoji ?? '🍅'}</span>
            <p className="text-[19px] font-bold text-ink-900">{round + 1}라운드</p>
            <p className="rounded-full bg-surface/80 px-3.5 py-1.5 text-[13.5px] font-medium text-ink-700 shadow-sm">
              {ROUND_INTROS[round]?.tip}
            </p>
          </div>
        ) : (
          <>
            {/* 수풀(뒤/앞) — 클릭은 통과한다. */}
            {roundPlan.foliage.map((spot, i) => (
              <FoliageCluster key={i} spot={spot} />
            ))}

            {/* 스폰 */}
            {roundPlan.spawns.map((s) => {
              const hit = hitsRef.current.get(s.id);
              if (hit?.result === 'harvest') return null; // 비행 요소가 대신 그려진다.
              if (elapsed < s.appearAt || elapsed > s.appearAt + s.lifeMs) return null;
              const { x, y } = spawnPosition(s, elapsed);
              const size = (isCritter(s.kind) ? 46 : 56) * s.scale;
              const penalized = hit?.result === 'penalty';
              return (
                <button
                  key={s.id}
                  type="button"
                  aria-label={SPAWN_LABELS[s.kind]}
                  disabled={penalized}
                  className={`absolute z-10 ${
                    penalized ? 'harvest-oops opacity-60 saturate-50' : 'harvest-pop cursor-pointer'
                  }`}
                  style={{
                    left: `${x}%`,
                    top: `${fieldTop(y)}%`,
                    // 터치 타깃 최소 44px — 생물(46×0.85≈39)엔 패딩으로 보전한다.
                    width: size,
                    height: size,
                    padding: isCritter(s.kind) ? 4 : 0,
                    marginLeft: -size / 2,
                    marginTop: -size / 2,
                  }}
                  onPointerDown={() => pick(s.id, s.kind, x, y, s.scale)}
                >
                  <SpawnSvg kind={s.kind} />
                </button>
              );
            })}

            {/* 감점 피드백 */}
            {floatsRef.current.map((f) => (
              <span
                key={f.key}
                className="harvest-float pointer-events-none absolute z-30 text-[16px] font-bold text-tomato-700"
                style={{ left: `${f.xPct}%`, top: `${fieldTop(f.yPct)}%` }}
              >
                {f.text}
              </span>
            ))}
          </>
        )}

        {/* 바구니로 날아가는 토마토 */}
        {flying.map((t) => (
          <span
            key={t.key}
            className="harvest-fly absolute z-40"
            style={
              {
                left: `${t.leftPct}%`,
                top: `${t.topPct}%`,
                width: 52 * t.scale,
                height: 52 * t.scale,
                marginLeft: -26 * t.scale,
                marginTop: -26 * t.scale,
                '--fly-dx': `${t.dx}px`,
                '--fly-dy': `${t.dy}px`,
              } as React.CSSProperties
            }
          >
            <span className="harvest-fly-inner block h-full w-full">
              <TomatoSvg kind="ripe" />
            </span>
          </span>
        ))}

        <Basket count={basketCount} />
      </div>

      {!mobileFull && (
        <p className="mt-3 text-center text-[12px] text-ink-400">
          빨갛게 익은 토마토만! 초록·주황·피망과 날아다니는 벌레는 감점이에요.
        </p>
      )}
    </div>
  );

  if (mobileFull) {
    return (
      <div className="fixed inset-0 z-[60] overflow-hidden bg-gradient-to-b from-[#eef6e6] to-[#f8faf5]">
        {/* 세로 파지: 90° 회전한 가로형 스테이지 / 실제 가로 파지: 그대로 풀스크린 */}
        <div className={rotated ? 'harvest-rotate-stage' : 'absolute inset-0'}>{stage}</div>
      </div>
    );
  }
  return stage;
}

// ─────────────────────────────────────────────────────────────
// 결과 화면 → 본체 연결
// ─────────────────────────────────────────────────────────────

function HarvestResult({
  result,
  onReplay,
  onLobby,
}: {
  result: FinishResponse;
  onReplay: () => void;
  onLobby: () => void;
}) {
  return (
    <div className="py-6">
      <div className="rounded-2xl bg-surface p-6 text-center shadow-card">
        <span className="text-[44px]">🧺</span>
        <h2 className="mt-2 text-[20px] font-semibold text-ink-900">수확 끝!</h2>
        {!result.rewarded && (
          <p className="mt-1 text-[12px] text-ink-400">
            오늘 {result.playNo}번째 판 · 연습이라 지급은 없어요
          </p>
        )}

        <div className="mx-auto mt-4 max-w-[260px] space-y-1.5 text-[14px]">
          <div className="flex justify-between text-ink-700">
            <span>수확한 토마토</span>
            <b className="tabular-nums">{result.picked}개</b>
          </div>
          <div className="flex justify-between text-ink-700">
            <span>점수</span>
            <b className="tabular-nums text-tomato-600">{result.score}점</b>
          </div>
          {result.fee > 0 && (
            <p className="pt-1 text-[12px] text-ink-400">
              농장 관리인이 수수료 {result.fee}개를 가져갔습니다…
            </p>
          )}
          <div className="flex justify-between border-t border-ink-100 pt-2 text-ink-900">
            <span>내 몫</span>
            {result.rewarded ? (
              <b className="tabular-nums">+{result.reward}🍅</b>
            ) : (
              <span className="text-[13px] text-ink-400">연습 판</span>
            )}
          </div>
        </div>

        {/* 판수 제한이 없으니 '한 판 더'는 늘 열려 있다. */}
        <div className="mt-5 flex justify-center gap-2">
          <Button onClick={onReplay}>한 판 더</Button>
          <Button variant="ghost" onClick={onLobby}>
            나가기
          </Button>
        </div>
      </div>

      {result.recentPosts.length > 0 && (
        <div className="mt-4 rounded-2xl bg-surface p-5 shadow-card">
          <p className="text-[13px] font-medium text-ink-500">쉬는 김에, 오늘의 새 글 🍃</p>
          <div className="mt-2 divide-y divide-ink-50">
            {result.recentPosts.map((p) => (
              <Link
                key={p.id}
                to={`/posts/${p.id}`}
                className="flex items-center gap-2 py-2.5 text-[14px] text-ink-900 transition hover:text-tomato-600"
              >
                <Badge>{BOARD_LABELS[p.board] ?? p.board}</Badge>
                <span className="min-w-0 flex-1 truncate">{p.title}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* 판이 끝날 때마다 이번 주 수확왕과 내 순위를 바로 보여준다(경쟁 훅). */}
      <Leaderboard />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 로비(회차 상태 + 리더보드)
// ─────────────────────────────────────────────────────────────

function Leaderboard() {
  const [board, setBoard] = useState<LeaderboardResponse | null>(null);

  useEffect(() => {
    void api
      .get<LeaderboardResponse>('/api/harvest/leaderboard')
      .then(setBoard)
      .catch(() => setBoard(null));
  }, []);

  if (!board) return null;

  return (
    <div className="mt-4 rounded-2xl bg-surface p-5 shadow-card">
      <div className="flex items-baseline justify-between">
        <p className="text-[15px] font-medium text-ink-900">주간 리더보드</p>
        <p className="text-[12px] text-ink-400">월요일마다 리셋 · 하루 베스트 합산</p>
      </div>

      {/* 이번 주 1위 = 수확왕. 리스트 위에 왕관을 씌워 크게 보여준다. */}
      {board.rows[0] && (
        <p className="mt-3 flex items-center gap-2 rounded-xl bg-tomato-50 px-3.5 py-2.5 text-[13.5px] text-tomato-700">
          <span className="text-[18px]">👑</span>
          <span>
            이번 주 수확왕 <b>{board.rows[0].nickname}</b> · {board.rows[0].score}점
          </span>
        </p>
      )}

      {board.rows.length === 0 ? (
        <p className="py-6 text-center text-[13px] text-ink-400">
          이번 주 첫 수확왕이 되어보세요!
        </p>
      ) : (
        <div className="mt-3 space-y-2">
          {board.rows.map((row) => (
            <div key={row.rank} className="flex items-center gap-2.5 text-[14px]">
              <span className="w-7 text-center tabular-nums text-ink-400">
                {row.rank <= 3 ? ['🥇', '🥈', '🥉'][row.rank - 1] : row.rank}
              </span>
              <span className="min-w-0 flex-1 truncate text-ink-900">{row.nickname}</span>
              {row.affiliation && (
                <Badge>{AFFILIATION_LABELS[row.affiliation as Affiliation] ?? row.affiliation}</Badge>
              )}
              <b className="tabular-nums text-ink-700">{row.score}점</b>
            </div>
          ))}
        </div>
      )}

      {board.me && (
        <p className="mt-3 border-t border-ink-50 pt-3 text-[13px] text-ink-500">
          내 순위: <b className="text-ink-900">{board.me.rank}위</b> · {board.me.score}점
        </p>
      )}
    </div>
  );
}

export function HarvestScreen() {
  const [status, setStatus] = useState<HarvestStatus | null>(null);
  const [session, setSession] = useState<StartResponse | null>(null);
  const [result, setResult] = useState<FinishResponse | null>(null);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);

  const loadStatus = useCallback(() => {
    void api
      .get<HarvestStatus>('/api/harvest/status')
      .then(setStatus)
      .catch(() => setError('수확철 정보를 불러오지 못했어요.'));
  }, []);

  useEffect(loadStatus, [loadStatus]);

  async function start() {
    setError('');
    setStarting(true);
    try {
      const res = await api.post<StartResponse>('/api/harvest/session/start', {});
      setResult(null);
      setSession(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '잠시 후 다시 시도해 주세요.');
      loadStatus();
    } finally {
      setStarting(false);
    }
  }

  // 플레이 중
  if (session && !result) {
    return (
      <HarvestGame
        session={session}
        onDone={(res) => {
          setResult(res);
          setSession(null);
          loadStatus();
        }}
        onAbort={(message) => {
          setError(message);
          setSession(null);
          loadStatus();
        }}
      />
    );
  }

  // 결과
  if (result) {
    return (
      <HarvestResult
        result={result}
        onReplay={() => void start()}
        onLobby={() => setResult(null)}
      />
    );
  }

  // 로비
  return (
    <div className="py-6">
      <h1 className="text-[26px] font-semibold tracking-tight text-ink-900">토마토 따기</h1>
      <p className="mt-1 text-[14px] text-ink-500">
        밭은 언제나 열려 있어요. 익은 토마토만 골라 바구니에 담으세요!
      </p>

      <div className="mt-5 rounded-2xl bg-surface p-6 text-center shadow-card">
        {!status ? (
          <Spinner />
        ) : (
          <>
            <span className="tomato-wiggle inline-block text-[44px]">🍅</span>
            {/* 남은 지급 판수는 "남았다"를 알리는 정보일 뿐, 플레이를 막지 않는다. */}
            <p className="mt-2 text-[17px] font-semibold text-ink-900">
              {status.rewardedLeft > 0 ? '수확하러 갈까요?' : '오늘 몫은 다 채웠어요'}
            </p>
            <p className="mt-1 text-[13px] text-ink-500">
              {status.rewardedLeft > 0
                ? `오늘 🍅 받는 판 ${status.rewardedLeft}판 남음 (하루 ${status.rewardedPlaysPerDay}판)`
                : '이제부터는 연습 판이에요 — 지급은 없지만 얼마든지 딸 수 있어요.'}
            </p>
            <div className="mt-4">
              <Button disabled={starting} onClick={() => void start()}>
                {starting ? '입장 중…' : status.rewardedLeft > 0 ? '수확 시작 🧺' : '연습하러 가기 🧺'}
              </Button>
            </div>
          </>
        )}
        <Notice>{error}</Notice>
      </div>

      <Leaderboard />
    </div>
  );
}
